import React from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Check, AlertTriangle, CalendarClock } from "lucide-react";
import { PLAN_PERKS, PLAN_LABEL, formatDate } from "./format";

export const CurrentPlanCard = ({
  plan, isPaid, renewIn, cancelScheduled, expiresAt, onCancel, onResume, resuming,
}) => (
  <section
    data-testid="current-plan-card"
    className={`relative overflow-hidden rounded-2xl border ${
      isPaid ? "border-transparent" : "border-gray-200 bg-white"
    }`}
  >
    {isPaid && <div className="absolute inset-0 bg-brand-gradient opacity-95" aria-hidden="true" />}
    <div className={`relative p-5 sm:p-7 lg:p-8 ${isPaid ? "text-white" : ""}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${isPaid ? "text-white/80" : "text-gray-500"}`}>
            Current plan
          </div>
          <div className="flex items-baseline gap-3 mt-2">
            <span className="font-display text-4xl sm:text-5xl tracking-[-0.03em] font-medium leading-none">
              {PLAN_LABEL[plan]}
            </span>
            {isPaid && (
              <span className="font-mono text-[10px] uppercase tracking-widest bg-white/20 text-white px-2 py-1 rounded">
                Active
              </span>
            )}
          </div>
          {isPaid && renewIn != null && !cancelScheduled && (
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-white/90">
              <CalendarClock size={14} strokeWidth={1.5} />
              Renews in {renewIn} day{renewIn === 1 ? "" : "s"} · {formatDate(expiresAt)}
            </div>
          )}
          {isPaid && cancelScheduled && (
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-white/95" data-testid="cancel-scheduled-banner">
              <AlertTriangle size={14} strokeWidth={1.8} />
              Cancelled · access ends {formatDate(expiresAt)}
            </div>
          )}
          {!isPaid && (
            <div className="mt-3 text-sm text-gray-600">
              You&apos;re on the Free plan. Upgrade any time — your saved packets and profile carry over.
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/pricing"
            data-testid="billing-upgrade"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isPaid
                ? "bg-white text-black hover:bg-gray-100"
                : "bg-black text-white hover:bg-gray-800"
            }`}
          >
            {isPaid ? "Change plan" : "Upgrade"}
            <ArrowRight size={14} strokeWidth={1.8} />
          </Link>
          {isPaid && !cancelScheduled && (
            <button
              data-testid="billing-cancel"
              onClick={onCancel}
              className="text-sm font-medium px-5 py-2.5 rounded-lg border border-white/30 text-white hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
          )}
          {isPaid && cancelScheduled && (
            <button
              data-testid="billing-resume"
              onClick={onResume}
              disabled={resuming}
              className="inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-lg bg-white text-black hover:bg-gray-100 disabled:opacity-60 transition-colors"
            >
              {resuming && <Loader2 size={14} className="animate-spin" />}
              Resume subscription
            </button>
          )}
        </div>
      </div>

      <div className={`mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm ${isPaid ? "text-white/90" : "text-gray-700"}`}>
        {(PLAN_PERKS[plan] || []).map((p) => (
          <div key={p} className="flex items-center gap-2">
            <Check size={14} strokeWidth={2} className={isPaid ? "text-white" : "text-[#7C3AED]"} />
            <span>{p}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);
